import React from 'react';
import { Tag as TagIcon, Plus, Trash2, Store, Save, Lock, Image as ImageIcon, Upload } from 'lucide-react';
import { Tag } from '../../types';
import { getAdminTags, createTag, deleteTag } from '../../api/tags';
import {
  getSellerProfile,
  updateSellerProfile,
  changeSellerPassword,
} from '../../api/auth';
import { uploadImage } from '../../api/upload';

export const SettingsTab: React.FC = () => {
  return (
    <div className="space-y-6">
      <SellerProfileManagement />
      <SellerPasswordManagement />
      <TagManagement />
    </div>
  );
};

const getErrorMessage = (error: any, fallback: string) => {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message[0] || fallback;
  if (typeof message === 'string') return message;
  return fallback;
};

const SellerProfileManagement = () => {
  const [businessName, setBusinessName] = React.useState('');
  const [image, setImage] = React.useState('');
  const [shareDescription, setShareDescription] = React.useState('');
  const [storeAddress, setAddress] = React.useState('');
  const [storeContact, setContact] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);
  const [isUploadingImage, setIsUploadingImage] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    getSellerProfile().then(data => {
      if (data) {
        setBusinessName(data.businessName || '');
        setImage(data.image || '');
        setShareDescription(data.shareDescription || '');
        setAddress(data.storeAddress || '');
        setContact(data.storeContact || '');
      }
    }).catch(console.error);
  }, []);

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploadingImage(true);
      const { url } = await uploadImage(file);
      setImage(url);
    } catch (error) {
      console.error(error);
      alert('이미지 업로드에 실패했습니다.');
    } finally {
      setIsUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await updateSellerProfile({
        businessName: businessName.trim() || undefined,
        image: image.trim() || null,
        shareDescription: shareDescription.trim() || null,
        storeAddress: storeAddress.trim() || undefined,
        storeContact: storeContact.trim() || undefined,
      });
      alert('판매자 정보가 저장되었습니다.');
    } catch (e) {
      console.error(e);
      alert('저장에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white p-8 rounded-[32px] shadow-sm border border-emerald-100 relative mb-6 overflow-hidden">
      <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50 rounded-full blur-3xl -mr-10 -mt-10 pointer-events-none" />

      <div className="flex items-center justify-between mb-8 relative">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-2xl">
            <Store size={20} />
          </div>
          <div>
            <h3 className="text-lg font-black text-gray-900">판매자 매장 정보</h3>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Seller Profile</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative">
        <div className="md:col-span-2">
          <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">대표 이미지</label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageUpload}
          />
          <div className="rounded-[28px] border border-gray-100 bg-gray-50/60 p-4 sm:p-5 flex flex-col sm:flex-row gap-4">
            <div className="w-full sm:w-40 aspect-[4/3] rounded-[24px] overflow-hidden border border-gray-100 bg-white flex items-center justify-center shrink-0">
              {image ? (
                <img
                  src={image}
                  alt="판매자 대표 이미지"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="flex flex-col items-center justify-center gap-2 text-gray-300">
                  <ImageIcon size={28} />
                  <span className="text-[11px] font-black">이미지 없음</span>
                </div>
              )}
            </div>

            <div className="flex-1 flex flex-col justify-between gap-3">
              <div>
                <p className="text-sm font-black text-gray-900">메신저 공유 대표 이미지</p>
                <p className="mt-1 text-[11px] font-bold text-gray-400 leading-relaxed">
                  카카오톡, 문자, SNS 링크 미리보기와 고객용 매장 정보 팝업에 우선 노출됩니다.
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingImage}
                  className="h-11 px-4 rounded-2xl bg-emerald-500 text-white text-xs font-black inline-flex items-center gap-2 shadow-lg shadow-emerald-500/15 disabled:opacity-50"
                >
                  {isUploadingImage ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>업로드 중...</span>
                    </>
                  ) : (
                    <>
                      <Upload size={16} />
                      <span>이미지 업로드</span>
                    </>
                  )}
                </button>

                {image && (
                  <button
                    type="button"
                    onClick={() => setImage('')}
                    className="h-11 px-4 rounded-2xl bg-white border border-gray-200 text-gray-500 text-xs font-black inline-flex items-center gap-2"
                  >
                    <Trash2 size={16} />
                    <span>이미지 제거</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">상호명</label>
          <input
            value={businessName}
            onChange={e => setBusinessName(e.target.value)}
            placeholder="상호명을 입력하세요"
            className="w-full h-12 px-4 rounded-2xl border border-gray-100 bg-gray-50/50 focus:bg-white focus:border-emerald-500 outline-none font-bold text-sm transition-all focus:shadow-lg focus:shadow-emerald-500/5"
          />
        </div>
        <div>
          <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">가게 주소</label>
          <input
            value={storeAddress}
            onChange={e => setAddress(e.target.value)}
            placeholder="가게 주소를 입력하세요"
            className="w-full h-12 px-4 rounded-2xl border border-gray-100 bg-gray-50/50 focus:bg-white focus:border-emerald-500 outline-none font-bold text-sm transition-all focus:shadow-lg focus:shadow-emerald-500/5"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">
            공유 메타 설명
          </label>
          <textarea
            value={shareDescription}
            onChange={e => setShareDescription(e.target.value)}
            placeholder="카카오톡, 문자, SNS 링크 미리보기에 우선 노출할 설명을 입력하세요"
            className="w-full min-h-[110px] px-4 py-3 rounded-2xl border border-gray-100 bg-gray-50/50 focus:bg-white focus:border-emerald-500 outline-none font-bold text-sm transition-all resize-none focus:shadow-lg focus:shadow-emerald-500/5"
          />
          <p className="mt-1.5 ml-1 text-[10px] font-bold text-gray-400">
            이미지는 위 대표 이미지를 그대로 사용하고, 이 설명만 공유 미리보기 전용으로 덮어씁니다.
          </p>
        </div>
        <div className="md:col-span-2">
          <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">연락처</label>
          <input
            value={storeContact}
            onChange={e => setContact(e.target.value)}
            placeholder="연락처를 입력하세요"
            className="w-full h-12 px-4 rounded-2xl border border-gray-100 bg-gray-50/50 focus:bg-white focus:border-emerald-500 outline-none font-bold text-sm transition-all focus:shadow-lg focus:shadow-emerald-500/5"
          />
        </div>
      </div>

      <div className="mt-8 flex justify-end relative">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="h-12 px-8 bg-emerald-500 hover:bg-emerald-600 text-white rounded-2xl font-black text-sm flex items-center justify-center transition-all shadow-lg shadow-emerald-500/20 active:scale-95 disabled:opacity-50"
        >
          {isSaving ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>저장 중...</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Save size={18} />
              <span>정보 저장하기</span>
            </div>
          )}
        </button>
      </div>
    </div>
  );
};

const SellerPasswordManagement = () => {
  const [currentPassword, setCurrentPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);

  const handleSave = async () => {
    if (!currentPassword) {
      alert('현재 비밀번호를 입력하세요.');
      return;
    }

    const normalizedNewPassword = newPassword.trim();
    if (normalizedNewPassword.length < 4) {
      alert('새 비밀번호는 4자 이상 입력하세요.');
      return;
    }

    if (normalizedNewPassword !== confirmPassword.trim()) {
      alert('새 비밀번호 확인이 일치하지 않습니다.');
      return;
    }

    try {
      setIsSaving(true);
      await changeSellerPassword(currentPassword, normalizedNewPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      alert('비밀번호가 변경되었습니다.');
    } catch (e) {
      console.error(e);
      alert(getErrorMessage(e, '비밀번호 변경에 실패했습니다.'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white p-8 rounded-[32px] shadow-sm border border-amber-100 relative overflow-hidden">
      <div className="absolute bottom-0 left-0 w-36 h-36 bg-amber-50 rounded-full blur-3xl -ml-10 -mb-10 pointer-events-none" />

      <div className="flex items-center justify-between mb-8 relative">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-amber-50 text-amber-600 rounded-2xl">
            <Lock size={20} />
          </div>
          <div>
            <h3 className="text-lg font-black text-gray-900">계정 비밀번호 변경</h3>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Seller Account Security</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative">
        <div>
          <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">현재 비밀번호</label>
          <input
            type="password"
            value={currentPassword}
            onChange={e => setCurrentPassword(e.target.value)}
            placeholder="현재 비밀번호를 입력하세요"
            className="w-full h-12 px-4 rounded-2xl border border-gray-100 bg-gray-50/50 focus:bg-white focus:border-amber-500 outline-none font-bold text-sm transition-all focus:shadow-lg focus:shadow-amber-500/5"
          />
        </div>
        <div>
          <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">새 비밀번호</label>
          <input
            type="password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            placeholder="4자 이상 입력하세요"
            className="w-full h-12 px-4 rounded-2xl border border-gray-100 bg-gray-50/50 focus:bg-white focus:border-amber-500 outline-none font-bold text-sm transition-all focus:shadow-lg focus:shadow-amber-500/5"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">새 비밀번호 확인</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            placeholder="새 비밀번호를 한 번 더 입력하세요"
            className="w-full h-12 px-4 rounded-2xl border border-gray-100 bg-gray-50/50 focus:bg-white focus:border-amber-500 outline-none font-bold text-sm transition-all focus:shadow-lg focus:shadow-amber-500/5"
          />
        </div>
      </div>

      <div className="mt-8 flex justify-end relative">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="h-12 px-8 bg-amber-500 hover:bg-amber-600 text-white rounded-2xl font-black text-sm flex items-center justify-center transition-all shadow-lg shadow-amber-500/20 active:scale-95 disabled:opacity-50"
        >
          {isSaving ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>변경 중...</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Save size={18} />
              <span>비밀번호 변경</span>
            </div>
          )}
        </button>
      </div>
    </div>
  );
};

const TagManagement = () => {
  const [tags, setTags] = React.useState<Tag[]>([]);
  const [newTagName, setNewTagName] = React.useState('');

  const refreshTags = React.useCallback(() => {
    getAdminTags().then(setTags).catch(console.error);
  }, []);

  React.useEffect(() => {
    refreshTags();
  }, [refreshTags]);

  const handleAddTag = async () => {
    if (!newTagName.trim()) return;
    try {
      await createTag(newTagName.trim());
      setNewTagName('');
      refreshTags();
    } catch (e) {
      console.error(e);
      alert('태그 생성 실패 (이미 이미 존재할 수 있습니다)');
    }
  };

  const handleDeleteTag = async (id: string) => {
    if (!confirm('태그를 삭제하시겠습니까?')) return;
    try {
      await deleteTag(id);
      refreshTags();
    } catch (e) {
      console.error(e);
      alert('태그 삭제 실패');
    }
  };

  return (
    <div className="bg-white p-8 rounded-[32px] shadow-sm border border-gray-100">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-purple-50 text-purple-600 rounded-2xl">
            <TagIcon size={20} />
          </div>
          <div>
            <h3 className="text-lg font-black text-gray-900">태그 및 분류 관리</h3>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Tag & Category Management</p>
          </div>
        </div>
        <div className="px-4 py-2 bg-gray-50 rounded-full text-[11px] font-black text-gray-500">
          총 {tags.length}개
        </div>
      </div>

      <div className="flex gap-3 max-w-md mb-10 group">
        <div className="flex-1 relative">
          <input
            type="text"
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            className="w-full h-12 pl-5 pr-4 rounded-2xl border border-gray-100 bg-gray-50/50 focus:bg-white focus:border-emerald-500 outline-none font-black text-sm transition-all focus:shadow-lg focus:shadow-emerald-500/5"
            placeholder="새로운 태그를 입력하세요"
            onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
          />
        </div>
        <button
          onClick={handleAddTag}
          className="h-12 px-6 bg-gray-900 text-white rounded-2xl flex items-center justify-center hover:bg-black transition-all shadow-lg shadow-gray-900/10 hover:shadow-gray-900/20 active:scale-95"
        >
          <Plus size={20} className="mr-2" />
          <span className="text-xs font-black">추가하기</span>
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        {tags.map((tag) => (
          <div
            key={tag.id}
            className="group flex items-center gap-3 px-4 py-2 bg-white border border-gray-100 rounded-2xl hover:border-emerald-500 hover:shadow-md transition-all cursor-default"
          >
            <span className="text-[13px] font-black text-gray-700 group-hover:text-emerald-600 transition-colors">#{tag.name}</span>
            <button
              onClick={() => handleDeleteTag(tag.id)}
              className="p-1 text-gray-300 hover:text-red-500 transition-all opacity-0 group-hover:opacity-100"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {tags.length === 0 && (
          <div className="w-full py-12 flex flex-col items-center justify-center bg-gray-50/50 rounded-[24px] border border-dashed border-gray-200">
            <TagIcon size={32} className="text-gray-200 mb-3" />
            <p className="text-xs font-bold text-gray-400">등록된 태그가 없습니다.</p>
          </div>
        )}
      </div>
    </div>
  );
};
